import React, { useEffect, useMemo } from "react";
import { Tree, Card, Form, Button, message, Tooltip, Modal, Dropdown } from "antd";
import { SaveOutlined, EditOutlined, StopOutlined, CheckOutlined, DownOutlined } from "@ant-design/icons";
import { ReactComponent as ZlbzFolderIcon } from '@/assets/svg/zlbz-folder.svg'
import { ReactComponent as ZlbzArticleIcon } from '@/assets/svg/zlbz-article.svg'
import { ToolBarType } from "../../../../type";
import { useShallow } from "zustand/react/shallow";
import useStore from "@/pages/documentCompile/store/useStore";
import { renderFormItem } from "@/pages/documentCompile/dict/help";
import { updateDataProject } from "@/pages/documentCompile/api/data-project";
import styles from "./index.module.less";
import { useSearchParams } from "react-router-dom";
import DomainCard from "../DomainCard";
import { DocumentFileType, DomainFieldConfig, DomainTreeNodeData } from "@/pages/documentCompile/type";
import DomainFormCard from "../DomainFormCard";
import useMethods from "@/pages/documentCompile/onlyofficeFieldSetting/hooks/usemethods";
import dayjs from "dayjs";
import clsx from "clsx";
import { DOC_URL, EXCEL_URL } from "@/pages/documentCompile/onlyofficeFieldSetting/config/sysConfig";
import { confirmAsync, confirmWithThreeButtons } from "@/utils/antd-help";
import { isLegalPreviewFileType } from "@/pages/documentCompile/utils/file";
import useInitData from "@/pages/documentCompile/onlyofficeFieldSetting/hooks/useInitData";
import { renderDocumentStatus } from "@/pages/documentCompile/utils/renderStatus";

interface MainBarProps {
  showLeft: boolean;
  open: boolean;
  isUnmountedRef: React.RefObject<boolean>;
}

const MainBar: React.FC<MainBarProps> = ({
  showLeft,
  open,
  isUnmountedRef,
}) => {
  const [projectForm] = Form.useForm();
  const setStoreState = useStore.setState;
  // 获取地址上面的projectId
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('projectId')
  const requestId = searchParams.get('requestId')
  const { handleAsyncReplaceFieldValue, refreshView, handlePackageFolderClick, doReplaceFieldValue } = useMethods()
  const { refreshPackageFolderTreeData, initDomainSettingData } = useInitData()
  const {
    selectedToolBar,
    packageFolderTreeData,
    packageFolderTreeDataExpandedKeys,
    domainTreeData,
    domainTreeDataExpandedKeys,
    selectedFileData,
    projectInfoDictData,
    projectInfoAttrValues,
    projectInfoFormEditable,
    setProjectInfoFormEditable,
    documentSettingProjectData,
    settingProjectFormData,
    isEditMode, // 修改为 isEditMode
    folderList,
    currentSettingDetailFolderDoc,
    currentDetailFolder
  } = useStore(
    useShallow((s) => ({
      selectedToolBar: s.selectedToolBar,
      packageFolderTreeData: s.packageFolderTreeData,
      packageFolderTreeDataExpandedKeys: s.packageFolderTreeDataExpandedKeys,
      domainTreeData: s.domainTreeData,
      domainTreeDataExpandedKeys: s.domainTreeDataExpandedKeys,
      selectedFileData: s.selectedFileData,
      projectInfoDictData: s.projectInfoDictData,
      projectInfoAttrValues: s.projectInfoAttrValues,
      projectInfoFormEditable: s.projectInfoFormEditable,
      setProjectInfoFormEditable: s.setProjectInfoFormEditable,
      documentSettingProjectData: s.documentSettingProjectData,
      settingProjectFormData: s.settingProjectFormData,
      isEditMode: s.isEditMode, // 修改为 isEditMode
      folderList: s.folderList,
      currentSettingDetailFolderDoc: s.currentSettingDetailFolderDoc,
      currentDetailFolder: s.currentDetailFolder
    }))
  );
  const selectedTreeKeys = selectedFileData?.id ? [selectedFileData?.id] : []
  // 处理文件夹切换

  // 处理树节点展开/收起事件
  const handlePackageFolderTreeExpand = (expandedKeys: React.Key[], info: any) => {
    setStoreState({
      packageFolderTreeDataExpandedKeys: expandedKeys as string[]
    });
  };

  const handleFormValuesChange = (changedFields: any, allFields: any) => {
    // console.log("changedFields", changedFields)
    // console.log("allFields", allFields)
    const localSettingProjectFormData = useStore.getState().settingProjectFormData
    // 直接使用 setState 更新表单数据
    setStoreState({
      settingProjectFormData: {
        ...localSettingProjectFormData,
        ...changedFields
      }
    });
  }

  // 保存项目信息
  const handleSaveProjectInfo = async () => {
    const settingProjectFormData = useStore.getState().settingProjectFormData
    const isExistEmptyProjectField = useStore.getState().isExistEmptyProjectField
    if (isExistEmptyProjectField) {
      const confirmed = await confirmAsync({
        title: '检测到有项目信息被清空，确认继续？',
        okText: '继续',
        cancelText: '取消'
      });
      if (!confirmed) {
        return;
      }
    }
    try {
      setStoreState({
        editorConfigLoading: true,
      });
      // const filterFormData = filterSubmitSettingFieldValue(allFormData)
      const projectAttrs = Object.keys(settingProjectFormData).map(key => {
        let fieldValue = settingProjectFormData[key] || ''
        if (dayjs.isDayjs(fieldValue)) {
          fieldValue = String(fieldValue.valueOf());
        }
        return {
          name: key,
          type: projectInfoDictData.find(item => item.dictCode === key)?.['attr_zdle'] || 'string',
          value: fieldValue
        }
      });
      doReplaceFieldValue();
      const params = {
        projectId: projectId,
        projectAttrs
      };
      await updateDataProject(params, requestId);
      await handleAsyncReplaceFieldValue()
      refreshPackageFolderTreeData()
      message.success('保存成功');
      setStoreState({
        editorConfigLoading: false,
      });
    } catch (error) {
      message.error('保存失败');
      console.error('保存项目信息失败:', error);
      setStoreState({
        editorConfigLoading: false,
      });
    }
  };

  // 切换编辑状态
  const handleToggleEdit = () => {
    setProjectInfoFormEditable(!projectInfoFormEditable);
  };

  // console.log("currentSettingDetailFolderDoc", currentSettingDetailFolderDoc)

  // 渲染文件夹切换下拉菜单
  const renderFolderDropdown = () => {
    const items = folderList?.map((folder) => ({
      key: folder.folderId.toString(),
      label: folder.folderName,
      onClick: () => refreshView(folder),
    })) || [];

    return (
      <Dropdown

        menu={{ items }}
        trigger={['click']}
        placement="bottomRight"
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          gap: '4px',
          fontSize: '12px',
          color: '#666'
        }}>
          <span>{currentSettingDetailFolderDoc?.folderName || currentDetailFolder?.folderName || '选择视图'}</span>
          <DownOutlined style={{ fontSize: '10px' }} />
        </div>
      </Dropdown>
    );
  };

  // 渲染项目信息卡片的额外操作
  const renderProjectInfoExtra = () => (
    <div style={{ display: 'flex', gap: '8px' }}>
      {/* <Tooltip title={projectInfoFormEditable ? "禁用编辑" : "启用编辑"}>
        <Button
          type="text"
          className={clsx("hover-icon")}
          size="small"
          icon={<EditOutlined />}
          onClick={handleToggleEdit}
          style={{ color: projectInfoFormEditable ? '#1890ff' : undefined }}
          disabled={!isEditMode}
        />
      </Tooltip> */}
      <Tooltip title={isEditMode ? "保存并同步" : "请先解锁编辑模式"}>
        <Button
          type="text"
          className={clsx("hover-icon")}
          size="small"
          icon={<CheckOutlined />}
          onClick={handleSaveProjectInfo}
          disabled={!isEditMode}
        />
      </Tooltip>
    </div>
  );

  // 初始化表单数据：只有在 store 中没有数据时才从 projectInfoAttrValues 同步
  // useEffect(() => {
  //   if (projectInfoAttrValues && Object.keys(settingProjectFormData).length === 0) {
  //     syncsettingProjectFormData();
  //   }
  // }, [projectInfoAttrValues, settingProjectFormData, syncsettingProjectFormData]);

  // 当 store 中的表单数据变化时，同步到 antd form

  console.log("settingProjectFormData", settingProjectFormData)

  useEffect(() => {
    if (selectedToolBar !== ToolBarType.PROJECT_INFO) {
      return
    }
    if (settingProjectFormData && Object.keys(settingProjectFormData).length > 0) {
      projectForm.setFieldsValue(settingProjectFormData);
    }

  }, [settingProjectFormData, selectedToolBar]); // 移除 form 依赖，因为 form 对象是稳定的

  // console.log("packageFolderTreeData", packageFolderTreeData)

  return (
    <div
      className={styles.container}
      style={{
        borderRight: showLeft ? "1px solid #f0f0f0" : "none",
      }}
    >
      {/* 资料包目录树 */}
      {selectedToolBar === ToolBarType.PACKAGE_INFO && (
        <Card
          className={styles.card}
          styles={{ body: { flex: 1, overflow: "auto" } }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>资料包目录</span>
              {renderFolderDropdown()}
            </div>
          }
          size="small"
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <div style={{ flex: 1, overflow: "auto", height: "100%" }}>
            {packageFolderTreeDataExpandedKeys.length > 0 ? <Tree
              className={styles.tree}
              treeData={packageFolderTreeData}
              onClick={(e, node: any) => handlePackageFolderClick(node)}
              onExpand={handlePackageFolderTreeExpand}
              showLine
              showIcon={true}
              titleRender={(node: any) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {node.folder ? <ZlbzFolderIcon /> : <ZlbzArticleIcon />}
                  {node.folder ? null : renderDocumentStatus(node.signStatus)}
                  <span>{node.name}</span>
                 
                </span>
              )}

              selectedKeys={selectedTreeKeys}
              blockNode
              expandedKeys={packageFolderTreeDataExpandedKeys}
            /> : null}

          </div>
        </Card>
      )}
      {/* 域字段列表 */}
      {selectedToolBar === ToolBarType.FIELD_DOMAIN && (
        <DomainCard />
      )}
      {selectedToolBar === ToolBarType.DOCUMENT_INFO && (
        <DomainFormCard requestId={requestId} isUnmountedRef={isUnmountedRef} open={open} />
      )}
      {/* 项目信息 */}
      {selectedToolBar === ToolBarType.PROJECT_INFO && (
        <Card
          className={styles.card}
          styles={{ body: { flex: 1, overflow: "auto" } }}
          title="项目信息"
          size="small"
          extra={renderProjectInfoExtra()}
        >
          <div style={{ flex: 1, overflow: "auto", height: "100%" }}>
            <Form form={projectForm} onValuesChange={handleFormValuesChange} layout="vertical" style={{ height: "100%" }}>
              {projectInfoDictData?.map((item) => (
                <Form.Item
                  key={item.dictCode}
                  name={item.dictCode}
                  label={item.dictName}
                  rules={[]}
                >
                  {renderFormItem(item, !projectInfoFormEditable)}
                </Form.Item>
              ))}
            </Form>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MainBar;
